import { useRouter } from 'next/router'
import React, { Fragment } from 'react'

import clsx from 'clsx'
import { isArray } from 'lodash'
import { UseFormReturn } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { autoPlacement, useFloating } from '@floating-ui/react'
import { Popover, Transition } from '@headlessui/react'

import { DEFAULT_CURRENCY } from '@/lib/constants'
import {
  RUB_STEP,
  RUB_STEP_PER_TH,
  USD_STEP,
  USD_STEP_PER_TH
} from '@/lib/constants'
import { formatCurrency } from '@/lib/utils'

import {
  DeviceFilterStore,
  useDeviceFilterStore
} from '@/hooks/stores/useDeviceFilterStore'
import useUserInfo from '@/hooks/users/useUserInfo'

import Button from '@/components/Button'
import Slider from '@/components/Slider'
import FormActions from '@/components/form/FormActions'
import SelectBoxButton from '@/components/selectbox/SelectBoxButton'

const DEFAULT_MAX_PRICE = 2000000

interface ShopPriceFilterModalModal {
  className?: string
  form: UseFormReturn<DeviceFilterStore>
  maxPrice?: number
  maxPricePerTH?: number
  minPrice?: number
  minPricePerTH?: number
}

export const ShopPriceFilterModal = ({
  className,
  form: { watch, setValue },
  maxPrice,
  maxPricePerTH,
  minPrice,
  minPricePerTH
}: ShopPriceFilterModalModal) => {
  const { locale } = useRouter()
  const { currency: userCurrency } = useUserInfo()
  const { refs, floatingStyles } = useFloating({
    middleware: [
      autoPlacement({
        alignment: 'end',
        allowedPlacements: ['bottom-start', 'bottom-end', 'top-start']
      })
    ]
  })

  const currency = userCurrency || DEFAULT_CURRENCY
  const isUSD = currency === 'USD'

  const { setPrices } = useDeviceFilterStore()

  const totalPrice = watch('totalPrice')
  const pricePerTH = watch('pricePerTH')

  const totalStart = totalPrice?.from
  const totalEnd = totalPrice?.to

  const pricePerTHStart = pricePerTH?.from
  const pricePerTHEnd = pricePerTH?.to

  const changePrices = () => {
    setPrices({ totalPrice, pricePerTH })
  }

  const arePricesDefault =
    totalStart === minPrice &&
    totalEnd === maxPrice &&
    pricePerTHStart === minPricePerTH &&
    pricePerTHEnd === maxPricePerTH

  const arePricesEmpty =
    arePricesDefault ||
    (!totalStart && !totalEnd && !pricePerTHStart && !pricePerTHEnd)

  const resetPrices = () => {
    const totalPrice = { from: minPrice as number, to: maxPrice as number }
    const pricePerTH = {
      from: minPricePerTH as number,
      to: maxPricePerTH as number
    }

    setPrices({ totalPrice, pricePerTH })
    setValue('totalPrice', totalPrice)
    setValue('pricePerTH', pricePerTH)
  }

  return (
    <div className={clsx('cursor-pointer', className)}>
      <Popover className="relative">
        <SelectBoxButton
          {...{
            variant: 'default',
            className:
              'border-[0.5px] !border-slate-300 !rounded-lg min-w-[156px] h-8',
            displayArrow: true,
            as: Popover.Button,
            ref: refs.setReference,
            hasResetButton: !arePricesEmpty,
            onReset: resetPrices
          }}
        >
          <span
            className={clsx(
              'max-w-[calc(min(100vw-72px,400px))] overflow-hidden overflow-ellipsis text-zink-800'
            )}
          >
            {arePricesEmpty ? (
              <FormattedMessage
                {...{
                  id: 'placeholder.price',
                  defaultMessage: 'Цена'
                }}
              />
            ) : (
              <FormattedMessage
                {...{
                  id: 'shop.filter.price.total',
                  defaultMessage:
                    'Общая цена {totalStart} - {totalEnd} | 1TH {pricePerTHStart} - {pricePerTHEnd}',
                  values: {
                    totalStart:
                      totalStart &&
                      formatCurrency(totalStart, currency, locale),
                    totalEnd:
                      totalEnd && formatCurrency(totalEnd, currency, locale),
                    pricePerTHStart:
                      pricePerTHStart &&
                      formatCurrency(pricePerTHStart, currency, locale),
                    pricePerTHEnd:
                      pricePerTHEnd &&
                      formatCurrency(pricePerTHEnd, currency, locale)
                  }
                }}
              />
            )}
          </span>
        </SelectBoxButton>
        <Transition
          as={Fragment}
          enter="transition-opacity duration-100 ease-in"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-100 ease-out"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Popover.Panel
            className="absolute left-0 z-[1] my-2 rounded-lg shadow-xl"
            ref={refs.setFloating}
            style={floatingStyles}
          >
            <div className="flex w-fit min-w-[300px] max-w-[calc(100vw-24px)] flex-col !rounded-lg bg-white phone-md:min-w-[350px] tablet:w-[480px]">
              <div className="flex flex-col gap-6 p-6">
                {maxPrice !== minPrice && (
                  <div className="flex flex-col">
                    <div className="flex items-center justify-between">
                      <FormattedMessage
                        {...{
                          id: 'shop.filter.price.total.modal',
                          defaultMessage: 'Общая цена'
                        }}
                      />
                      <span className="font-medium">
                        {totalStart &&
                          formatCurrency(totalStart, currency, locale)}{' '}
                        –{' '}
                        {totalEnd && formatCurrency(totalEnd, currency, locale)}
                      </span>
                    </div>

                    <Slider
                      {...{
                        initialValue: minPrice || 0,
                        step: isUSD ? USD_STEP : RUB_STEP,
                        minValue: minPrice || 0,
                        maxValue: maxPrice
                          ? maxPrice + 1000 * 100
                          : DEFAULT_MAX_PRICE,
                        ...(totalStart !== null &&
                          totalEnd !== null && {
                            defaultValue: [totalStart, totalEnd]
                          }),
                        thumbLabels: [totalStart, totalEnd],
                        onChange: (values: number | number[]) => {
                          if (isArray(values)) {
                            setValue('totalPrice', {
                              from: values[0],
                              to: values[1]
                            })
                          }
                        },
                        ariaLabel: 'Total price'
                      }}
                    />
                  </div>
                )}
                {maxPricePerTH !== minPricePerTH && (
                  <div className="flex flex-col">
                    <div className="flex items-center justify-between">
                      <FormattedMessage
                        {...{
                          id: 'shop.filter.price.hashrate',
                          defaultMessage: 'Цена за 1 TH'
                        }}
                      />
                      <span className="font-medium">
                        {pricePerTHStart &&
                          formatCurrency(
                            pricePerTHStart,
                            currency,
                            locale
                          )}{' '}
                        –{' '}
                        {pricePerTHEnd &&
                          formatCurrency(pricePerTHEnd, currency, locale)}
                      </span>
                    </div>
                    <Slider
                      {...{
                        initialValue: minPricePerTH || 0,
                        step: isUSD ? USD_STEP_PER_TH : RUB_STEP_PER_TH,
                        minValue: minPricePerTH || 0,
                        maxValue: maxPricePerTH
                          ? maxPricePerTH + 10 * 100
                          : DEFAULT_MAX_PRICE,
                        ...(pricePerTHStart !== null &&
                          pricePerTHEnd !== null && {
                            defaultValue: [pricePerTHStart, pricePerTHEnd]
                          }),
                        thumbLabels: ['start', 'end'],
                        onChange: (values: number | number[]) => {
                          if (isArray(values)) {
                            setValue('pricePerTH', {
                              from: values[0],
                              to: values[1]
                            })
                          }
                        },
                        ariaLabel: 'Price per TH'
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="border-t-[0.5px] border-slate-300">
                <FormActions className="ml-auto w-fit grid-cols-2 px-6 py-4">
                  <Popover.Button>
                    <Button
                      className="phone-md:w-36"
                      tag="div"
                      variant="secondary"
                    >
                      <FormattedMessage
                        {...{ id: 'button.cancel', defaultMessage: 'Отмена' }}
                      />
                    </Button>
                  </Popover.Button>
                  <Popover.Button>
                    <Button
                      className="phone-md:w-36"
                      onClick={changePrices}
                      tag="div"
                    >
                      <FormattedMessage
                        {...{ id: 'button.apply', defaultMessage: 'Применить' }}
                      />
                    </Button>
                  </Popover.Button>
                </FormActions>
              </div>
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
    </div>
  )
}
